import { BaseSyntheticEvent, useEffect, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { Text, Button } from '@restworld/ui-ds-base';
import { getAssetSrc, useCountDownTimer } from '@restworld/utils-common';

import { ModalHeader, ModalSubHeader } from './common';

export type EmailSubmittedContentProps = {
  email?: string;
  loading?: boolean;
  remainingTimes?: number;
  onFormSubmit?: (e?: BaseSyntheticEvent) => Promise<void>;
};

export function useEmailSubmittedContent({ email, remainingTimes, loading, onFormSubmit }: EmailSubmittedContentProps) {
  const { t } = useTranslation('user-settings');

  const { hasExpired, label, resetTimer } = useCountDownTimer({ timeInSeconds: 15 });

  useEffect(() => {
    if (remainingTimes > 0) {
      resetTimer();
    }
  }, [remainingTimes, resetTimer]);

  const header = t('email_update_email_submitted_step_header');

  const emailSubmittedContent = useMemo(
    () => (
      <div className="flex flex-col items-center">
        <Image src={getAssetSrc('/utils-assets/success-check.svg')} width={64} height={64} alt={header} />
        <div className="flex flex-col items-center justify-start gap-2 mb-10 mt-2">
          <ModalHeader>{header}</ModalHeader>
          <ModalSubHeader>
            {t('email_update_email_submitted_step_subheader')} <span className="font-bold">{email}</span>.
          </ModalSubHeader>
        </div>
      </div>
    ),
    [email, header, t]
  );

  const emailSubmittedFooter = useMemo(
    () => (
      <div className="flex flex-col w-full items-center gap-2">
        <Button
          onClick={onFormSubmit}
          fullwidth={true}
          variant="secondary-neutral"
          loading={loading}
          disabled={remainingTimes === 0 || !hasExpired}
        >
          {hasExpired
            ? t('email_update_email_submitted_step_resend_email')
            : `${t('email_update_email_submitted_step_resend_invite')} ${label}`}
        </Button>
        {remainingTimes < 3 && (
          <Text color="muted">
            {remainingTimes} {t('email_update_email_submitted_step_resend_click_count')}{' '}
          </Text>
        )}
      </div>
    ),
    [onFormSubmit, loading, remainingTimes, hasExpired, t, label]
  );
  return {
    emailSubmittedContent,
    emailSubmittedFooter,
  };
}
